import { useTranslations } from "@/lib/useTranslations";
import { useMemo, useState, type KeyboardEvent } from "react";

import { Button } from "@/components/ui/Button";
import { TeamMemberDialog } from "@/components/ui/admin/team/TeamMemberDialog";
import type { TeamMember } from "@/core/services/types/team.types";
import { useAuth } from "@/lib/auth";

type SortDirection = "asc" | "desc";
type SortKey = "name" | "email" | "role";

function formatFullName(member: TeamMember): string {
  return `${member.first_name} ${member.last_name}`.trim();
}

function formatRole(
  role: TeamMember["role"],
  t: (key: string) => string,
): string {
  return role === "ADMIN" ? t("admin") : t("editor");
}

function getInitials(member: TeamMember): string {
  const first = member.first_name.charAt(0).toUpperCase();
  const last = member.last_name.charAt(0).toUpperCase();
  return `${first}${last}`;
}

function compareMembers(
  a: TeamMember,
  b: TeamMember,
  key: SortKey,
  direction: SortDirection,
): number {
  let cmp = 0;

  if (key === "name") {
    cmp = formatFullName(a).localeCompare(formatFullName(b));
  } else if (key === "email") {
    cmp = a.email.localeCompare(b.email);
  } else {
    cmp = a.role.localeCompare(b.role);
  }

  return direction === "asc" ? cmp : -cmp;
}

function toggleSort(
  current: { key: SortKey; direction: SortDirection },
  key: SortKey,
): { key: SortKey; direction: SortDirection } {
  if (current.key === key) {
    return { key, direction: current.direction === "asc" ? "desc" : "asc" };
  }
  return { key, direction: "asc" };
}

function StatusBadge({ isActive }: { isActive: boolean }) {
  const t = useTranslations("common");

  return (
    <span
      className={`inline-flex rounded px-2 py-0.5 text-xs font-medium ${
        isActive
          ? "bg-emerald-50 text-emerald-700"
          : "bg-neutral-100 text-neutral-500"
      }`}
    >
      {isActive ? t("active") : t("inactive")}
    </span>
  );
}

function RoleBadge({ role }: { role: TeamMember["role"] }) {
  const t = useTranslations("team.roles");

  return (
    <span
      className={`inline-flex rounded px-2 py-0.5 text-xs font-medium ${
        role === "ADMIN"
          ? "bg-violet-50 text-violet-700"
          : "bg-sky-50 text-sky-700"
      }`}
    >
      {formatRole(role, t)}
    </span>
  );
}

function SortableHeader({
  label,
  sortKey,
  activeSort,
  onSort,
}: {
  label: string;
  sortKey: SortKey;
  activeSort: { key: SortKey; direction: SortDirection };
  onSort: (key: SortKey) => void;
}) {
  const isActive = activeSort.key === sortKey;

  return (
    <th className="px-4 py-3 text-left">
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        className={`inline-flex items-center gap-1 uppercase tracking-wider transition-colors hover:text-whart-text ${
          isActive ? "text-whart-text" : "text-neutral-500"
        }`}
      >
        <span>{label}</span>
        <span className="inline-flex flex-col text-[9px] leading-[0.65]">
          <span
            className={
              isActive && activeSort.direction === "asc"
                ? "text-whart-dark"
                : "text-neutral-300"
            }
          >
            ▲
          </span>
          <span
            className={
              isActive && activeSort.direction === "desc"
                ? "text-whart-dark"
                : "text-neutral-300"
            }
          >
            ▼
          </span>
        </span>
      </button>
    </th>
  );
}

interface TeamMembersPanelProps {
  members: TeamMember[];
}

function openMemberFromKeyboard(
  event: KeyboardEvent<HTMLTableRowElement>,
  onOpen: () => void,
) {
  if (event.key === "Enter" || event.key === " ") {
    event.preventDefault();
    onOpen();
  }
}

export function TeamMembersPanel({ members }: TeamMembersPanelProps) {
  const t = useTranslations("team");
  const tCommon = useTranslations("common");
  const { isAdmin } = useAuth();
  const canManageTeam = isAdmin;
  const [sort, setSort] = useState<{
    key: SortKey;
    direction: SortDirection;
  }>({ key: "name", direction: "asc" });
  const [selectedMember, setSelectedMember] = useState<TeamMember | null>(null);
  const [isCreateOpen, setIsCreateOpen] = useState(false);

  const sortedMembers = useMemo(
    () =>
      [...members].sort((a, b) => compareMembers(a, b, sort.key, sort.direction)),
    [members, sort],
  );

  return (
    <>
    <section className="wireframe-border bg-whart-bg">
      <div className="flex items-center justify-between gap-3 border-b border-black/10 px-4 py-3">
        <p className="text-sm text-neutral-600">{t("memberCount", { count: members.length })}</p>
        {canManageTeam ? (
          <Button onClick={() => setIsCreateOpen(true)}>{t("create")}</Button>
        ) : null}
      </div>

      {members.length === 0 ? (
        <p className="px-4 py-8 text-center text-sm text-neutral-500">
          {t("empty")}
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[40rem] table-fixed text-left text-sm">
            <colgroup>
              <col className="w-16" />
              <col />
              <col />
              <col className="w-28" />
              <col className="w-28" />
            </colgroup>
            <thead>
              <tr className="wireframe-border-b bg-neutral-50 text-xs font-medium">
                <th className="px-4 py-3" aria-label={tCommon("avatar")} />
                <SortableHeader
                  label={tCommon("name")}
                  sortKey="name"
                  activeSort={sort}
                  onSort={(key) => setSort((current) => toggleSort(current, key))}
                />
                <SortableHeader
                  label={tCommon("email")}
                  sortKey="email"
                  activeSort={sort}
                  onSort={(key) => setSort((current) => toggleSort(current, key))}
                />
                <SortableHeader
                  label={tCommon("role")}
                  sortKey="role"
                  activeSort={sort}
                  onSort={(key) => setSort((current) => toggleSort(current, key))}
                />
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-neutral-500">
                  {tCommon("status")}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {sortedMembers.map((member) => (
                <tr
                  key={member.id}
                  tabIndex={canManageTeam ? 0 : undefined}
                  role={canManageTeam ? "button" : undefined}
                  aria-label={
                    canManageTeam
                      ? t("viewMember", { name: formatFullName(member) })
                      : undefined
                  }
                  className={
                    canManageTeam
                      ? "cursor-pointer hover:bg-neutral-50 focus-visible:bg-neutral-50 focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-whart-dark"
                      : undefined
                  }
                  onClick={
                    canManageTeam ? () => setSelectedMember(member) : undefined
                  }
                  onKeyDown={
                    canManageTeam
                      ? (event) =>
                          openMemberFromKeyboard(event, () =>
                            setSelectedMember(member),
                          )
                      : undefined
                  }
                >
                  <td className="px-4 py-3">
                    {member.avatar_url ? (
                      // eslint-disable-next-line @next/next/no-img-element
                      <img
                        src={member.avatar_url}
                        alt=""
                        className="size-9 rounded-full object-cover"
                      />
                    ) : (
                      <span
                        aria-hidden
                        className="inline-flex size-9 items-center justify-center rounded-full bg-neutral-100 text-xs font-medium text-neutral-600"
                      >
                        {getInitials(member)}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 font-medium text-whart-text">
                    {formatFullName(member)}
                  </td>
                  <td className="px-4 py-3 text-neutral-600">{member.email}</td>
                  <td className="px-4 py-3">
                    <RoleBadge role={member.role} />
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge isActive={member.is_active} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>

    {canManageTeam ? (
      <TeamMemberDialog
        open={isCreateOpen || selectedMember !== null}
        member={selectedMember}
        onClose={() => {
          setSelectedMember(null);
          setIsCreateOpen(false);
        }}
        onMemberCreated={() => {
          setIsCreateOpen(false);
        }}
        onMemberUpdated={(updated) => setSelectedMember(updated)}
      />
    ) : null}
    </>
  );
}
